/*
 * Settings → Operations → Wireline & Slickline (WL-1, fieldiq-api#1336).
 *
 * Two sections:
 *
 *  - The company defaults (GET / PUT /wireline-slickline/settings): the
 *    default ticket mode, the day-sheet start time (the tenant's local wall
 *    clock, HH:MM), the default day-sheet length, the depth unit (or follow
 *    the company unit system), and the misfire note printed on day sheets.
 *  - One customer's settings (GET / PUT /wireline-slickline/customers/:id/
 *    settings): its own ticket mode or "Company default", and who signs.
 *  - The run rule (GET / PUT /wireline-slickline/run-settings, WL-2,
 *    fieldiq-api#1338): whether a unit's first run of the day REQUIRES a
 *    passing rig-up pressure test on its day sheet. Off, a missing test is a
 *    warning on the run; on, the run is refused (409 rig_up_test_required).
 *  - The required crew certificates (GET / PUT /wireline-slickline/settings/
 *    required-certificates, WL-6, fieldiq-api#1362, 0627): the certificates
 *    the compliance digest checks every hand on an open job for. No list
 *    stated = the default (H2S Alive, First Aid, TDG). PUT replaces the whole
 *    set; an empty list is refused by the API in its own words. The list only
 *    feeds the digest — it holds nobody off a job — and a certificate with no
 *    expiry date recorded is a warning there, never held or expired.
 *
 * A tenant (or customer) with no row reads the API's defaults with
 * `configured: false`, and the page says so. Only changed fields are sent.
 * Writes are admin / office staff (WL_ADMIN); everyone else reads with the
 * controls disabled. A 403 is said as a refusal, never as a saved toast.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  useUpdateWlCustomerSettings,
  useUpdateWlSettings,
  useWlCustomers,
  useWlCustomerSettings,
  useWlMeta,
  useWlSettings,
} from '../../hooks/useWirelineSlickline';
import { useUpdateWlRunSettings, useWlRunSettings } from '../../hooks/useWlJobs';
import { useUpdateWlRequiredCertificates, useWlRequiredCertificates } from '../../hooks/useWlAi';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { wlBlankToNull, canAdminWl, wlCatalogLabel, wlToInput, wlRefusal, WL_TIME_PATTERN } from '../../lib/wireline-slickline';
import type { WlCatalog, WlCustomerSettings, WlCustomerSettingsUpdate, WlSettings, WlSettingsUpdate } from '../../types/wireline-slickline';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';

function ErrorLine({ message, testId }: { message: string | null; testId?: string }) {
  if (!message) return null;
  return (
    <p role="alert" data-testid={testId} className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-white block mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-fieldiq-muted mt-1">{hint}</p>}
    </div>
  );
}

/** Hours: > 0 and at most 24 (mirrors the API). Returns the number, or a sentence. */
function parseHours(text: string, label: string): number | string {
  const t = text.trim();
  const n = Number(t);
  if (t === '' || !/^\d+(\.\d+)?$/.test(t) || !Number.isFinite(n) || n <= 0 || n > 24) {
    return `${label} must be a number of hours greater than 0 and at most 24, e.g. 12.`;
  }
  return n;
}

/** The options a catalog select offers; the stored value stays selectable when the catalog lacks it. */
function options(list: { value: string; label: string }[] | undefined, current: string): { value: string; label: string }[] {
  const entries = list ?? [];
  return current && !entries.some((e) => e.value === current) ? [{ value: current, label: current }, ...entries] : entries;
}

function CompanyDefaults({ settings, catalog, canWrite }: { settings: WlSettings; catalog: WlCatalog | null; canWrite: boolean }) {
  const save = useUpdateWlSettings();
  const initial = {
    mode: settings.defaultTicketMode ?? '',
    start: settings.daySheetStartTime ?? '',
    hours: wlToInput(settings.defaultDaySheetHours),
    depth: settings.depthUnit ?? '',
    misfire: settings.misfireNote ?? '',
  };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = (Object.keys(form) as (keyof typeof form)[]).some((k) => form[k].trim() !== initial[k].trim());

  const onSave = () => {
    setError(null);
    const body: WlSettingsUpdate = {};
    if (form.mode && form.mode !== initial.mode) body.default_ticket_mode = form.mode;
    if (form.start.trim() !== initial.start) {
      if (!WL_TIME_PATTERN.test(form.start.trim())) return setError('The day-sheet start time must be a 24-hour time, e.g. 07:00.');
      body.day_sheet_start_time = form.start.trim();
    }
    if (form.hours.trim() !== initial.hours) {
      const h = parseHours(form.hours, 'The default day-sheet length');
      if (typeof h === 'string') return setError(h);
      body.default_day_sheet_hours = h;
    }
    // Blank follows the company unit system: sent as null, never as "".
    if (form.depth !== initial.depth) body.depth_unit = form.depth === 'm' || form.depth === 'ft' ? form.depth : null;
    if (form.misfire.trim() !== initial.misfire.trim()) body.misfire_note = wlBlankToNull(form.misfire);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(body, {
      onSuccess: () => toast.success('Wireline & slickline settings saved'),
      onError: (err) => setError(wlRefusal(err, 'The settings could not be saved.')),
    });
  };

  return (
    <section className="space-y-3" data-testid="wl-company-settings">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-white">Company defaults</h2>
        {canWrite && (
          <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
      {!settings.configured && (
        <p className="text-xs text-fieldiq-muted" data-testid="wl-settings-defaults">
          These are the defaults. Nobody has saved wireline &amp; slickline settings for this company yet.
        </p>
      )}
      <ErrorLine message={error} testId="wl-settings-error" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field id="wl-ticket-mode" label="Default ticket mode" hint="Used when a customer has no setting of its own.">
          <select id="wl-ticket-mode" className={inputCls} value={form.mode} disabled={!canWrite} onChange={(e) => set('mode', e.target.value)}>
            {!form.mode && <option value="">—</option>}
            {options(catalog?.ticketModes, form.mode).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="wl-day-start" label="Day-sheet start time" hint="24-hour, the company's local time, e.g. 07:00.">
          <input id="wl-day-start" className={inputCls} value={form.start} placeholder="07:00" disabled={!canWrite} onChange={(e) => set('start', e.target.value)} />
        </Field>
        <Field id="wl-day-hours" label="Default day-sheet length (hours)" hint="More than 0, at most 24.">
          <input id="wl-day-hours" className={inputCls} inputMode="decimal" value={form.hours} disabled={!canWrite} onChange={(e) => set('hours', e.target.value)} />
        </Field>
        <Field id="wl-depth-unit" label="Depth unit" hint="Blank follows the company's unit system.">
          <select id="wl-depth-unit" className={inputCls} value={form.depth} disabled={!canWrite} onChange={(e) => set('depth', e.target.value)}>
            <option value="">Company unit system</option>
            {(catalog?.lengthUnits?.length ? catalog.lengthUnits : ['m', 'ft']).map((u) => (
              <option key={u} value={u}>
                {u === 'm' ? 'Metres (m)' : u === 'ft' ? 'Feet (ft)' : u}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field id="wl-misfire-note" label="Misfire note" hint="Your own wording for how a misfire is handled, printed on the day sheets. Blank when none.">
        <textarea id="wl-misfire-note" className={`${inputCls} h-20`} maxLength={2000} value={form.misfire} disabled={!canWrite} onChange={(e) => set('misfire', e.target.value)} />
      </Field>
    </section>
  );
}

function CustomerSettingsForm({
  cs,
  catalog,
  companyMode,
  canWrite,
}: {
  cs: WlCustomerSettings;
  catalog: WlCatalog | null;
  companyMode: string | null;
  canWrite: boolean;
}) {
  const save = useUpdateWlCustomerSettings();
  const initial = { mode: cs.ticketMode ?? '', name: cs.signerName ?? '', title: cs.signerTitle ?? '' };
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setForm(initial);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = form.mode !== initial.mode || form.name.trim() !== initial.name || form.title.trim() !== initial.title;

  const onSave = () => {
    setError(null);
    const body: WlCustomerSettingsUpdate = {};
    // An empty choice is "Company default": sent as null, never as "".
    if (form.mode !== initial.mode) body.ticket_mode = form.mode || null;
    if (form.name.trim() !== initial.name) body.signer_name = wlBlankToNull(form.name);
    if (form.title.trim() !== initial.title) body.signer_title = wlBlankToNull(form.title);
    if (Object.keys(body).length === 0) return setError('Nothing has changed.');
    save.mutate(
      { customerId: cs.customerId, body },
      {
        onSuccess: () => toast.success(`Settings saved for ${cs.customerName ?? 'the customer'}`),
        onError: (err) => setError(wlRefusal(err, 'The customer settings could not be saved.')),
      },
    );
  };

  const defaultLabel = `Company default (${wlCatalogLabel(catalog?.ticketModes, companyMode)})`;

  return (
    <div className="space-y-3" data-testid="wl-customer-settings-form">
      {!cs.configured && (
        <p className="text-xs text-fieldiq-muted">Nothing saved for this customer yet: the company default applies.</p>
      )}
      <ErrorLine message={error} testId="wl-customer-settings-error" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Field
          id="wl-cust-ticket-mode"
          label="Ticket mode"
          hint={`Applies now: ${wlCatalogLabel(catalog?.ticketModes, cs.effectiveTicketMode)}`}
        >
          <select id="wl-cust-ticket-mode" className={inputCls} value={form.mode} disabled={!canWrite} onChange={(e) => set('mode', e.target.value)}>
            <option value="">{defaultLabel}</option>
            {options(catalog?.ticketModes, form.mode).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>
        <Field id="wl-cust-signer-name" label="Signer name" hint="Who signs this customer's tickets. Blank when not known.">
          <input id="wl-cust-signer-name" className={inputCls} maxLength={200} value={form.name} disabled={!canWrite} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field id="wl-cust-signer-title" label="Signer title" hint="e.g. Company man, Wellsite supervisor.">
          <input id="wl-cust-signer-title" className={inputCls} maxLength={100} value={form.title} disabled={!canWrite} onChange={(e) => set('title', e.target.value)} />
        </Field>
      </div>
      {canWrite && (
        <div className="flex justify-end">
          <button type="button" onClick={onSave} disabled={!dirty || save.isPending} className={btnPrimary}>
            {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
            {save.isPending ? 'Saving...' : 'Save customer'}
          </button>
        </div>
      )}
    </div>
  );
}

function CustomerSettings({ catalog, companyMode, canWrite }: { catalog: WlCatalog | null; companyMode: string | null; canWrite: boolean }) {
  const customers = useWlCustomers();
  const [customerId, setCustomerId] = useState('');
  const cs = useWlCustomerSettings(customerId || undefined);
  const customersFailure = queryLoadFailure(customers);
  const csFailure = queryLoadFailure(cs);

  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="wl-customer-settings">
      <h2 className="text-base font-bold text-white">Customer settings</h2>
      <p className="text-xs text-fieldiq-muted">A customer may take its own ticket mode and name who signs its tickets.</p>
      {customers.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customers" />
      ) : customersFailure ? (
        <LoadFailureNotice error={queryFailureError(customers)} kind={customersFailure} subject="your customers" />
      ) : (customers.data ?? []).length === 0 ? (
        <p className="text-sm text-fieldiq-text">There are no customers yet. Add one under Clients first.</p>
      ) : (
        <>
          <div className="max-w-sm">
            <Field id="wl-cust-picker" label="Customer">
              <select id="wl-cust-picker" className={inputCls} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Select a customer…</option>
                {(customers.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!customerId ? null : cs.isLoading ? (
            <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading customer settings" />
          ) : csFailure || !cs.data?.data ? (
            <LoadFailureNotice error={queryFailureError(cs)} kind={csFailure ?? 'unreachable'} subject="this customer's settings" />
          ) : (
            <CustomerSettingsForm key={customerId} cs={cs.data.data} catalog={catalog} companyMode={companyMode} canWrite={canWrite} />
          )}
        </>
      )}
    </section>
  );
}

/** The rig-up rule toggle. Saved on change; a refusal is said, never toasted as saved. */
function RunRules({ canWrite }: { canWrite: boolean }) {
  const query = useWlRunSettings();
  const update = useUpdateWlRunSettings();
  const [error, setError] = useState<string | null>(null);
  const failure = queryLoadFailure(query);
  const settings = query.data?.data ?? null;
  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="wl-run-settings">
      <div>
        <h2 className="text-base font-semibold text-white">Runs</h2>
        <p className="text-xs text-fieldiq-muted mt-1">
          A unit&rsquo;s first run of the day is checked for a passing rig-up pressure test on its day sheet.
        </p>
      </div>
      {query.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading run settings" />
      ) : failure || !settings ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="the run settings" />
      ) : (
        <>
          <label className="flex items-start gap-3 text-sm text-white">
            <input
              type="checkbox"
              className="mt-1"
              aria-label="Require a rig-up test before the first run"
              data-testid="wl-rig-up-required"
              checked={settings.rigUpTestRequired}
              disabled={!canWrite || update.isPending}
              onChange={(e) => {
                setError(null);
                update.mutate(e.target.checked, {
                  onSuccess: () => toast.success('Run settings saved'),
                  onError: (err) => setError(wlRefusal(err, 'The run settings could not be saved.')),
                });
              }}
            />
            <span>
              Require a passing rig-up pressure test before a unit&rsquo;s first run of the day
              <span className="block text-xs text-fieldiq-muted mt-0.5">
                {settings.rigUpTestRequired
                  ? 'On: a first run with no passing rig-up test is refused until one is recorded on the sheet.'
                  : 'Off: the run saves and the sheet carries a warning for the office to check.'}
                {!settings.configured ? ' (Not set yet: this is the default.)' : ''}
              </span>
            </span>
          </label>
          <ErrorLine message={error} testId="wl-run-settings-error" />
        </>
      )}
    </section>
  );
}

/**
 * The crew certificates the compliance digest requires. Admin / office staff
 * tick and save; everyone else reads the ticks disabled. The saved list is the
 * API's answer, never the local ticks: after a save the boxes re-seed from it.
 */
function RequiredCertificates({ canWrite }: { canWrite: boolean }) {
  const query = useWlRequiredCertificates();
  const update = useUpdateWlRequiredCertificates();
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[] | null>(null);
  const failure = queryLoadFailure(query);
  const data = query.data ?? null;
  const saved = (data?.certificates ?? []).map((c) => c.key);
  useEffect(() => {
    if (data) setPicked(data.certificates.map((c) => c.key));
  }, [data]);
  const current = picked ?? saved;
  const changed = current.length !== saved.length || current.some((k) => !saved.includes(k));
  const catalog = data?.catalog ?? [];
  const defaultLabels = (data?.defaultKeys ?? []).map((k) => catalog.find((c) => c.key === k)?.label ?? k).join(', ');
  const toggle = (key: string, on: boolean) => {
    setError(null);
    setPicked((prev) => {
      const base = prev ?? saved;
      return on ? (base.includes(key) ? base : [...base, key]) : base.filter((k) => k !== key);
    });
  };
  const save = (keys: string[]) => {
    setError(null);
    update.mutate(keys, {
      onSuccess: () => toast.success('Required certificates saved'),
      onError: (err) => setError(wlRefusal(err, 'The required certificates could not be saved.')),
    });
  };
  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="wl-required-certs">
      <div>
        <h2 className="text-base font-semibold text-white">Required crew certificates</h2>
        <p className="text-xs text-fieldiq-muted mt-1">
          The compliance digest checks every hand on an open wireline job for each certificate ticked here. It lists what is missing, expired or
          due; it does not take anyone off a job. A certificate with no expiry date recorded is shown as a warning, never as expired.
        </p>
      </div>
      {query.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading required certificates" />
      ) : failure || !data ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="the required certificates" />
      ) : (
        <>
          {data.isDefault && (
            <p className="text-xs text-fieldiq-muted" data-testid="wl-required-certs-default">
              Not set yet: the default list applies{defaultLabels ? ` (${defaultLabels})` : ''}.
            </p>
          )}
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="wl-required-certs-list">
            {catalog.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm text-white">
                <input
                  type="checkbox"
                  aria-label={c.label}
                  data-testid={`wl-required-cert-${c.key}`}
                  checked={current.includes(c.key)}
                  disabled={!canWrite || update.isPending}
                  onChange={(e) => toggle(c.key, e.target.checked)}
                />
                {c.label}
              </label>
            ))}
          </div>
          <ErrorLine message={error} testId="wl-required-certs-error" />
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={btnPrimary}
                disabled={!changed || update.isPending}
                onClick={() => save(current)}
                data-testid="wl-required-certs-save"
              >
                {update.isPending && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                Save required certificates
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default function WirelineSlicklineSettingsPage() {
  const { user } = useAuth();
  const canWrite = canAdminWl(user?.role);
  const query = useWlSettings();
  const catalog = useWlMeta().data?.data ?? null;
  const settings = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading wireline & slickline settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="wireline & slickline settings" />
      </div>
    );
  }

  return (
    <div className="min-h-full" data-testid="wl-settings-page">
      <div className="px-6 py-4 space-y-4">
        {!canWrite && (
          <p className="text-xs text-fieldiq-muted" data-testid="wl-settings-readonly">
            Only an admin or office staff can change wireline & slickline settings.
          </p>
        )}
        <CompanyDefaults settings={settings} catalog={catalog} canWrite={canWrite} />
        <CustomerSettings catalog={catalog} companyMode={settings.defaultTicketMode} canWrite={canWrite} />
        <RunRules canWrite={canWrite} />
        <RequiredCertificates canWrite={canWrite} />
      </div>
    </div>
  );
}
